import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { CheckCircle2, Loader2, Phone, Mail, User as UserIcon, MessageSquare, ShieldCheck, Sparkles } from "lucide-react";
import { COUNTRIES } from "@/lib/countries";
import { BrandMark } from "@/components/shared/BrandMark";

interface CapturePage {
  channel_id: string;
  channel_display_name: string;
  channel_handle?: string | null;
  platform: string;
  brand_name?: string | null;
  brand_website?: string | null;
  brand_support_email?: string | null;
  brand_signature?: string | null;
}

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const platformLabel = (p: string) =>
  ({ youtube: "YouTube", instagram: "Instagram", facebook: "Facebook",
     twitter: "X", linkedin: "LinkedIn", tiktok: "TikTok", threads: "Threads",
   } as Record<string, string>)[p] || p;

export default function LeadCapturePublic() {
  const { channelId = "" } = useParams();
  const [params] = useSearchParams();
  const ref = params.get("ref") || undefined;

  const [meta, setMeta] = useState<CapturePage | null>(null);
  const [metaError, setMetaError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [countryCode, setCountryCode] = useState("US");
  const [mobile, setMobile] = useState("");
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [company, setCompany] = useState(""); // honeypot

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API_BASE}/lead-capture/${encodeURIComponent(channelId)}`);
        if (!cancelled) setMeta(r.data);
      } catch (e: any) {
        if (!cancelled) setMetaError(e?.response?.data?.detail || "This link is not valid.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [channelId]);

  const dial = COUNTRIES.find((c) => c.code === countryCode)?.dial || "+1";
  const mobileFull = `${dial}${mobile.replace(/^\+?/, "").replace(/[\s\-().]/g, "")}`;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim() || !email.trim() || !mobile.trim()) {
      setError("Please fill your name, email and mobile number.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_BASE}/lead-capture/${encodeURIComponent(channelId)}`, {
        name: name.trim(),
        email: email.trim(),
        mobile: mobileFull,
        mobile_country: countryCode,
        message: message.trim() || undefined,
        ref,
        website, // honeypot
        company, // honeypot
      });
      setSubmitted(true);
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const channelName = meta?.channel_display_name || "our team";
  const brandName = meta?.brand_name || "SociBaba";
  const pageTitle = `Contact ${channelName} · ${brandName}`;

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-[#0f172a] via-[#1e1b4b] to-[#0b1120] text-white flex items-center justify-center px-4 py-10">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {/* Decorative blobs */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-fuchsia-500/20 blur-3xl" />
        <div className="absolute -bottom-32 -right-24 h-[28rem] w-[28rem] rounded-full bg-violet-500/20 blur-3xl" />
      </div>

      <div className="relative w-full max-w-xl" data-testid="lead-capture-public">
        <div className="flex items-center justify-center gap-2 mb-6">
          <div className="h-9 w-9 rounded-lg bg-white/10 backdrop-blur flex items-center justify-center">
            <Sparkles className="h-5 w-5 text-white" />
          </div>
          <BrandMark
            showIcon
            iconClassName="h-[1.15em] w-[1.15em] -mt-[2px] mr-2 inline-block align-middle invert"
            className="font-serif-display text-xl font-bold tracking-tight flex items-center"
          />
        </div>

        {loading ? (
          <div className="rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10 p-10 text-center">
            <Loader2 className="h-6 w-6 animate-spin mx-auto text-white/70" />
          </div>
        ) : metaError ? (
          <div
            className="rounded-2xl bg-white/5 backdrop-blur-xl border border-rose-400/30 p-10 text-center"
            data-testid="lead-capture-error"
          >
            <div className="text-rose-300 font-semibold mb-1">Invalid capture link</div>
            <div className="text-sm text-white/70">{metaError}</div>
          </div>
        ) : submitted ? (
          <div
            className="rounded-2xl bg-white/5 backdrop-blur-xl border border-emerald-400/30 p-10 text-center"
            data-testid="lead-capture-success"
          >
            <div className="mx-auto h-14 w-14 rounded-full bg-emerald-500/20 flex items-center justify-center mb-4">
              <CheckCircle2 className="h-8 w-8 text-emerald-300" />
            </div>
            <h2 className="text-2xl font-display font-bold mb-2">You're on the list ✨</h2>
            <p className="text-white/70 text-sm">
              Thanks {name.split(" ")[0]}. {channelName} will reach out on the number or email you shared — usually within 24 hours.
            </p>
          </div>
        ) : (
          <div className="rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10 p-8 sm:p-10 shadow-2xl">
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-fuchsia-300 mb-2">
                <ShieldCheck className="h-3 w-3" /> Secure contact form
              </div>
              <h1 className="text-2xl sm:text-3xl font-display font-bold leading-tight" data-testid="lead-capture-title">
                Talk to <span className="text-fuchsia-300">{channelName}</span>
              </h1>
              <p className="text-sm text-white/70 mt-2">
                Share your details and {channelName}
                {meta?.platform ? ` (${platformLabel(meta.platform)})` : ""} will get in touch. No spam — we
                only use this to reply to your enquiry.
              </p>
            </div>

            <form onSubmit={submit} className="space-y-4" data-testid="lead-capture-form">
              {/* Honeypot fields — hidden from real users, visible to bots */}
              <div className="absolute -left-[9999px] top-0" aria-hidden="true">
                <input type="text" tabIndex={-1} autoComplete="off"
                  value={website} onChange={(e) => setWebsite(e.target.value)} />
                <input type="text" tabIndex={-1} autoComplete="off"
                  value={company} onChange={(e) => setCompany(e.target.value)} />
              </div>

              <label className="block">
                <div className="text-xs font-medium text-white/70 mb-1.5 flex items-center gap-1.5">
                  <UserIcon className="h-3.5 w-3.5" /> Your name
                </div>
                <input
                  data-testid="lc-name"
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Priya Sharma"
                  className="w-full h-11 px-3.5 rounded-lg bg-white/10 border border-white/15 focus:border-fuchsia-400 focus:bg-white/15 outline-none transition placeholder:text-white/40"
                />
              </label>

              <label className="block">
                <div className="text-xs font-medium text-white/70 mb-1.5 flex items-center gap-1.5">
                  <Mail className="h-3.5 w-3.5" /> Email
                </div>
                <input
                  data-testid="lc-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="priya@example.com"
                  className="w-full h-11 px-3.5 rounded-lg bg-white/10 border border-white/15 focus:border-fuchsia-400 focus:bg-white/15 outline-none transition placeholder:text-white/40"
                />
              </label>

              <label className="block">
                <div className="text-xs font-medium text-white/70 mb-1.5 flex items-center gap-1.5">
                  <Phone className="h-3.5 w-3.5" /> Mobile number
                </div>
                <div className="flex gap-2">
                  <select
                    data-testid="lc-country"
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    className="h-11 px-2 rounded-lg bg-white/10 border border-white/15 focus:border-fuchsia-400 focus:bg-white/15 outline-none transition text-sm w-28"
                  >
                    {COUNTRIES.map((c) => (
                      <option key={c.code} value={c.code} className="bg-slate-900">
                        {c.code} {c.dial}
                      </option>
                    ))}
                  </select>
                  <input
                    data-testid="lc-mobile"
                    type="tel"
                    required
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value)}
                    placeholder="98765 43210"
                    className="flex-1 h-11 px-3.5 rounded-lg bg-white/10 border border-white/15 focus:border-fuchsia-400 focus:bg-white/15 outline-none transition placeholder:text-white/40"
                  />
                </div>
              </label>

              <label className="block">
                <div className="text-xs font-medium text-white/70 mb-1.5 flex items-center gap-1.5">
                  <MessageSquare className="h-3.5 w-3.5" /> How can we help? <span className="text-white/40">(optional)</span>
                </div>
                <textarea
                  data-testid="lc-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={3}
                  maxLength={2000}
                  placeholder="Tell us a little about your enquiry…"
                  className="w-full px-3.5 py-2.5 rounded-lg bg-white/10 border border-white/15 focus:border-fuchsia-400 focus:bg-white/15 outline-none transition placeholder:text-white/40 resize-none"
                />
              </label>

              {error && (
                <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-400/30 rounded-md px-3 py-2" data-testid="lc-error">
                  {error}
                </div>
              )}

              <button
                type="submit"
                data-testid="lc-submit"
                disabled={submitting}
                className="w-full h-12 rounded-lg bg-gradient-to-r from-fuchsia-500 to-violet-500 text-white font-semibold hover:opacity-95 disabled:opacity-60 disabled:cursor-not-allowed transition inline-flex items-center justify-center gap-2 group"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                {submitting ? "Sending…" : "Get in touch"}
              </button>
              <p className="text-[11px] text-white/50 text-center mt-2">
                By submitting, you agree to be contacted by {channelName}. We never share your details.
              </p>
            </form>
          </div>
        )}

        <div className="mt-6 text-center text-[11px] text-white/40">
          Powered by <BrandMark className="font-semibold text-white/60" /> · Lead Capture
        </div>
      </div>
    </div>
  );
}
